import { css, html, LitElement, nothing, type PropertyValues } from "lit";
import { repeat } from "lit/directives/repeat.js";
import Sortable from "sortablejs";
import { z } from "zod";
import { checkNotNil } from "../../check-not-nil";
import { JsonBootstrapper } from "../../handlers/json-bootstrapper";
import { FormAssociationMixin } from "../../mixins/form-associated-element";
import {
  type ParserSchema,
  type ParserType,
  parser,
} from "../../schemas/parser";
import { TaskChecklist, TaskChecklistItem } from "../../schemas/task";
import { hostStyles } from "../../styles/host.styles";
import { WmElement } from "../element";
import { WmChecklistItem } from "./item";

const TAG_NAME = "wm-checklist";

type ChecklistItem = ParserType<typeof ChecklistItem>;
const ChecklistItem = parser(
  z.strictObject({
    id: z.string(),
    value: TaskChecklistItem.schema,
  }),
);

type ChecklistItems = ParserType<typeof ChecklistItems>;
const ChecklistItems = parser(z.array(ChecklistItem.schema));

/**
A component that delineates wa-inputs.

It provides a button group for the user to add or remove items. Items are
marked as required. They all have the same name, so they are provided to
the backend as an array.

It isn't using the shadow DOM on purpose. It turns out, it's incredibly
hacky to get the keyboard navigation right (tab and shift+tab).
*/
// TODO: make it screen reader friendly.
export class WmChecklist extends FormAssociationMixin(WmElement) {
  static readonly TAG_NAME = TAG_NAME;

  static properties = {
    ...super.properties,
    items: { state: true },
    label: { reflect: true },
  };

  static styles = [
    hostStyles,
    /*
    Originally copied and adapted from Web Awesome's native styles
    https://github.com/shoelace-style/webawesome/blob/next/packages/webawesome/src/styles/native.css
    */
    css`
      fieldset {
        min-width: 0;
        padding: var(--wa-space-l);
        padding-block-start: calc(var(--wa-space-l) - 0.5em);

        border: solid 1px var(--wa-form-control-border-color);
        border-radius: var(--wa-form-control-border-radius);
      }

      legend {
        padding: 0;
        padding-inline: var(--wa-space-xs);
        font-weight: var(--wa-form-control-label-font-weight);
      }

      .list {
        display: flex;
        flex-direction: column;
        margin-block: 0;
        margin-block-end: var(--wa-space-xs);
        margin-inline: 0;
        gap: var(--wa-space-xs);
        align-items: stretch;
        justify-content: flex-start;
      }
    `,
  ];

  static shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };

  /**
  An array of items where the key is their unique generated identifier, and the
  value is the form value of their corresponding checklist item.
  */
  declare items: ChecklistItems;
  declare readonly label: string;
  #sortable?: Sortable;
  #nextId: number;
  #invalidTarget?: WmChecklistItem;
  #invalidFired: boolean;
  #jsonBootstrapper: JsonBootstrapper<ParserSchema<typeof TaskChecklist>>;

  constructor() {
    super();
    this.items = [];
    this.label = "";
    this.#sortable = undefined;
    this.#nextId = 0;
    this.#invalidTarget = undefined;
    this.#invalidFired = false;
    this.#jsonBootstrapper = new JsonBootstrapper({
      schema: TaskChecklist.schema,
      callback: (checklist) => {
        this.checklist = checklist;
      },
      logger: this.logger,
    });
    this.addEventListener("invalid", () => {
      this.#invalidFired = true;
    });
    this.addEventListener("focus", () => {
      // TODO: prevent default an implement the default behavior?
      // TODO: this should be doable using only the validation target. No
      // need to play with that focus bullshit.
      this.focus();
    });
  }

  get checklist(): TaskChecklist {
    return this.items.map((i) => i.value);
  }

  set checklist(v: TaskChecklist) {
    const result = TaskChecklist.safeParse(v);
    if (result.error != null) {
      this.logger.warn("invalid checklist, ignoring", v, result.error);
      return;
    }

    const items = [];
    for (const value of result.data) {
      items.push({
        id: (this.#nextId++).toString(10),
        value,
      });
    }
    this.items = items;
  }

  reportError(index: number, message: string) {
    const item = this.#queryNthChecklistItem(index);
    item.reportError(message);
  }

  disconnectedCallback(): void {
    this.#sortable?.destroy();
    this.#sortable = undefined;
    super.disconnectedCallback();
  }

  focus() {
    // Only override the behavior if we're in the process of focusing an
    // invalid form element.
    if (this.#invalidFired) {
      this.#invalidTarget?.focus();
    }
    this.#invalidFired = false;
  }

  protected render() {
    return html`
      <slot @slotchange=${(e: Event) => this.#jsonBootstrapper.slotChanged(e)}></slot>
      <fieldset class="wa-stack">
        <legend>${this.label}</legend>
        ${this.#renderItems()}
        <wa-button class="wa-align-self-start" appearance="filled" size="s" variant="brand" @click=${this.#appendItem}>Add item</wa-button>
      </fieldset>
      `;
  }

  protected updated(properties: PropertyValues<this>): void {
    if (properties.has("items")) {
      this.#updateForm();
      if (this.#sortable == null && this.items.length !== 0) {
        const inputList = checkNotNil(
          this.renderRoot.querySelector(".list"),
        ) as HTMLElement;
        let oldIndex = 0;
        let newIndex = 0;
        this.#sortable = Sortable.create(inputList, {
          animation: 150,
          handle: ".handle",
          onStart: (e) => {
            oldIndex = checkNotNil(e.oldIndex);
          },
          // Do not actually update the DOM, this interferes with Lit caching.
          onMove: (e) => {
            newIndex = [...inputList.children].indexOf(e.related);
            return false;
          },
          onEnd: (_) => this.#handleDrag(oldIndex, newIndex),
        });
        // Reset sortable when there is no div anymore.
      } else if (this.items.length === 0) {
        if (this.#sortable != null) {
          this.#sortable.destroy();
          this.#sortable = undefined;
        }
      }
    }
  }

  #appendItem() {
    this.items.push({
      id: (this.#nextId++).toString(10),
      // TODO: need to get the default from checklist item.
      value: {
        description: "",
        isChecked: false,
      },
    });
    this.requestUpdate("items");
  }

  #renderItems() {
    return html`
        ${
          this.items.length > 0
            ? html`
            <div class="list">
              ${repeat(
                this.items,
                (item) => item.id,
                (item) => this.#renderItem(item),
              )}
            </div>
          `
            : nothing
        }`;
  }

  #renderItem(item: ChecklistItem) {
    // TODO: size = "s" here?
    return html`
      <wm-checklist-item
        data-id="${item.id}"
        ?checked=${item.value.isChecked}
        description=${item.value.description}
        @wm-checklist-item-updated=${() => this.#updateItemWithId(item.id)}
        @wm-checklist-item-removed=${() => this.#removeItemWithId(item.id)}
      ></wm-checklist-item>
      `;
  }

  #removeItemWithId(id: string) {
    this.items = this.items.filter((i) => i.id !== id);
  }

  #updateItemWithId(id: string) {
    const found = this.items.find((i) => i.id === id);
    if (found == null) {
      this.logger.warn("received item updated for untracked item");
      return;
    }
    const elements = this.renderRoot.querySelectorAll(WmChecklistItem.TAG_NAME);
    const element = elements.values().find((i) => i.dataset.id === id);
    if (element == null) {
      this.logger.warn("receive item updated but missing item element");
      return;
    }

    found.value = element.value;
    this.requestUpdate("items");
  }

  // Must be called after rendering as we inspect children.
  #updateForm() {
    const elements = this.#queryChecklistItems();
    if (this.name) {
      const formData = new FormData();
      for (const e of elements) {
        formData.append(this.name, JSON.stringify(e.value));
      }
      this.internals.setFormValue(formData);
    }
    // TODO: handle race condition by queuing this and running in batches?
    // This must happen asynchronously and after the full update has been
    // performed due to how wa-input handles its own validity constraints.
    // We can only assume they are correctly updated after a full update.
    Promise.all(elements.map((e) => e.getUpdateComplete())).then(() => {
      for (const e of elements) {
        // If one of them is invalid, we can break early.
        if (!e.validationTarget.checkValidity()) {
          this.#invalidTarget = e;
          this.internals.setValidity(
            e.validationTarget.validity,
            e.validationTarget.validationMessage,
            e,
          );
          return;
        }
      }
      // If none of them are invalid, then we are good :sunglasses:.
      this.#invalidTarget = undefined;
      this.internals.setValidity({});
    });
  }

  #handleDrag(oldIndex: number, newIndex: number) {
    if (oldIndex === newIndex) {
      return;
    }
    const newItems: Array<ChecklistItem> = [];
    const minBound = Math.min(oldIndex, newIndex);
    const maxBound = Math.max(oldIndex, newIndex);
    const isForward = oldIndex < newIndex;
    // We already know where the dragged element ends up, so we can
    // already set it there.
    newItems[newIndex] = this.items[oldIndex];
    // Everything outside the bounds, remain the same.
    let i = 0;
    while (i < minBound) {
      newItems[i] = this.items[i];
      i++;
    }
    i = maxBound + 1;
    while (i < this.items.length) {
      newItems[i] = this.items[i];
      i++;
    }
    // Everything within the bounds shifted, either left or right depending
    // on direction.
    if (isForward) {
      i = oldIndex;
      while (i < newIndex) {
        newItems[i] = this.items[i + 1];
        i++;
      }
    } else {
      i = oldIndex;
      while (i > newIndex) {
        newItems[i] = this.items[i - 1];
        i--;
      }
    }
    this.items = newItems;
  }

  #queryChecklistItems() {
    return [...this.renderRoot.querySelectorAll(WmChecklistItem.TAG_NAME)];
  }

  #queryNthChecklistItem(index: number): WmChecklistItem {
    return this.queryNotNil(`wm-checklist-item:nth-of-type(${index + 1})`);
  }
}

customElements.define(TAG_NAME, WmChecklist);

declare global {
  interface HTMLElementTagNameMap {
    [TAG_NAME]: WmChecklist;
  }
}
